/**
 * Feature Disabled Page Component
 *
 * Displayed when a feature is disabled via feature toggle.
 * Provides user-friendly message explaining the feature is unavailable.
 *
 * @module frontend/src/components/FeatureDisabledPage
 */

import { Container, Title, Text, Stack, Paper, Button, Center, ThemeIcon } from '@mantine/core';
import { IconLock, IconHome, IconArrowLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Layout } from './Layout';
import { useAuth } from '../../features/auth';

// ========================================
// TYPES
// ========================================

interface FeatureDisabledPageProps {
  /**
   * Name of the disabled feature (displayed in title)
   */
  featureName: string;

  /**
   * Optional description explaining why the feature is disabled
   */
  description?: string;

  /**
   * Whether to show a back button (default: true)
   */
  showBackButton?: boolean;

  /**
   * Whether to show a home button (default: true)
   */
  showHomeButton?: boolean;

  /**
   * Custom action button (replaces default buttons)
   */
  customAction?: React.ReactNode;
}

// ========================================
// COMPONENT
// ========================================

/**
 * Feature Disabled Page Component
 *
 * Shows a friendly message when a feature is disabled.
 * Used with FeatureGate component to handle disabled toggles.
 *
 * @example
 * ```tsx
 * <FeatureGate
 *   toggleId="pro_calculator_enabled"
 *   fallback={<FeatureDisabledPage featureName="Kalkulator Profesjonalny" />}
 * >
 *   <ProCalculator />
 * </FeatureGate>
 * ```
 */
export function FeatureDisabledPage({
  featureName,
  description,
  showBackButton = true,
  showHomeButton = true,
  customAction,
}: FeatureDisabledPageProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const defaultDescription = `Ta funkcja jest obecnie niedostępna. Prosimy o cierpliwość - pracujemy nad tym!`;

  return (
    <Layout isAuthenticated={isAuthenticated}>
      <Container size="sm" py="xl">
        <Center style={{ minHeight: '50vh' }}>
          <Paper p="xl" radius="md" withBorder style={{ maxWidth: 500, width: '100%' }}>
            <Stack align="center" gap="lg">
              {/* Icon */}
              <ThemeIcon size={80} radius="xl" color="gray" variant="light">
                <IconLock size={40} stroke={1.5} />
              </ThemeIcon>

              {/* Title */}
              <Title order={2} ta="center">
                {featureName}
              </Title>

              {/* Description */}
              <Text ta="center" c="dimmed" size="lg">
                {description || defaultDescription}
              </Text>

              {/* Status Badge */}
              <Text size="sm" c="dimmed" ta="center">
                Status: Funkcja tymczasowo wyłączona
              </Text>

              {/* Actions */}
              {customAction ? (
                customAction
              ) : (
                <Stack gap="xs" w="100%">
                  {showBackButton && (
                    <Button
                      variant="light"
                      leftSection={<IconArrowLeft size={18} />}
                      onClick={() => navigate(-1)}
                      fullWidth
                    >
                      Wróć do poprzedniej strony
                    </Button>
                  )}
                  {showHomeButton && (
                    <Button
                      variant="outline"
                      leftSection={<IconHome size={18} />}
                      onClick={() => navigate('/')}
                      fullWidth
                    >
                      Przejdź do strony głównej
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Paper>
        </Center>
      </Container>
    </Layout>
  );
}

// ========================================
// PRE-CONFIGURED VARIANTS
// ========================================

/**
 * Pro Calculator Disabled Page
 */
export function ProCalculatorDisabled() {
  return (
    <FeatureDisabledPage
      featureName="Kalkulator Profesjonalny"
      description="Kalkulator profesjonalny jest obecnie niedostępny. Możesz skorzystać z podstawowego kalkulatora."
    />
  );
}

/**
 * Hourly Import Disabled Page
 */
export function HourlyImportDisabled() {
  return (
    <FeatureDisabledPage
      featureName="Import danych godzinowych"
      description="Import danych godzinowych z Tauron eLicznik jest obecnie niedostępny. Prosimy spróbować później."
    />
  );
}

/**
 * Save Calculation Disabled Message
 */
export function SaveCalculationDisabled() {
  return (
    <Paper p="md" withBorder radius="md" bg="gray.0">
      <Stack align="center" gap="sm">
        <ThemeIcon size={40} radius="xl" color="gray" variant="light">
          <IconLock size={20} />
        </ThemeIcon>
        <Text ta="center" c="dimmed" size="sm">
          Zapisywanie kalkulacji jest tymczasowo niedostępne.
        </Text>
      </Stack>
    </Paper>
  );
}

/**
 * G14 Optimization Disabled Message
 */
export function G14OptimizationDisabled() {
  return (
    <Paper p="md" withBorder radius="md" bg="gray.0">
      <Stack align="center" gap="sm">
        <ThemeIcon size={40} radius="xl" color="gray" variant="light">
          <IconLock size={20} />
        </ThemeIcon>
        <Text ta="center" c="dimmed" size="sm">
          Panel optymalizacji G14 jest tymczasowo niedostępny.
        </Text>
      </Stack>
    </Paper>
  );
}
